import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'reactstrap';

import adminSelectors from 'src/redux/admin/admin-selectors';
import { userTransaction } from 'src/redux/admin/admin-slice';
import { generateTransaction } from 'src/redux/redux-helpers';
import { StoreState } from 'src/redux/store';
import { UserTransactions } from 'src/types/admin';
import { Role } from 'src/types/auth';
import { Collection, CollectionRole } from 'src/types/collection';
import { CRUD } from 'src/types/core';
import PillDropdown from './PillDropdown';

interface CollectionRoleDropdownProps {
  collection: Collection;
}

const UpdateCollectionRoleDropdown = ({
  collection,
}: CollectionRoleDropdownProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentMember = useSelector(adminSelectors.getCurrentUser);
  const iAmAdminOrOwner = useSelector(adminSelectors.iAmAdminOrOwner);
  const isAdminOrOwner = useSelector(adminSelectors.isAdminOrOwner);
  const isSenseMaker = useSelector(adminSelectors.isOrganizationSenseMaker);
  const transactions = useSelector((state) =>
    adminSelectors.getUserTransactionsById(
      state as StoreState,
      currentMember?.id
    )
  );
  const [transaction, setTransaction] = React.useState<
    UserTransactions | undefined
  >();
  const [tooltip, setTooltip] = React.useState<boolean>(false);

  const roleFilter = (role: Role) => {
    return (
      role.collection_id === collection.id &&
      Object.values(CollectionRole)
        // remove host role and lowercase the rest
        .map((role) => (role === CollectionRole.host ? '' : role.toLowerCase()))
        .includes(role.role_type as CollectionRole)
    );
  };
  const collectionRole = (
    isAdminOrOwner
      ? CollectionRole.manager
      : // TODO: remove this org sensemaker check once the catalog sensemaker migration lands
      // and all org sensemakers are given the highlighter role for all the collections in their org
      isSenseMaker
      ? CollectionRole.highlighter
      : currentMember?.roles.filter(roleFilter)[0]?.role_type || 'None'
  ) as CollectionRole | 'None';

  // check if the user has the host role directly
  const isAssignedHost =
    currentMember &&
    currentMember.roles.filter(
      (r) =>
        r.role_type.toLowerCase() === CollectionRole.host.toLowerCase() &&
        r.collection_id === collection.id
    ).length > 0;

  React.useEffect(() => {
    if (transaction) {
      const matchingTransaction = transactions.filter(
        (item) => item.id === transaction.id
      );
      if (!matchingTransaction.length) {
        setTransaction(undefined);
      } else {
        setTransaction(matchingTransaction[0]);
      }
    }
  }, [transactions, transaction]);

  const handleCollectionRoleChange = (collectionRole: string) => {
    if (currentMember) {
      let roles: Role[];

      // build the roles array
      if (collectionRole === 'None') {
        roles = [];
      } else {
        // Set specific collection role and preserve host role if they have it
        roles = [
          {
            role_type: collectionRole.toLowerCase(),
            collection_id: collection.id,
          },
        ];
        // if the user is a host, they should retain that role
        if (isAssignedHost) {
          roles.push({
            role_type: CollectionRole.host.toLowerCase(),
            collection_id: collection.id,
          });
        }
      }

      const transaction = generateTransaction({
        type: 'update_user_collection_role',
        crud: CRUD.put,
        data: {
          id: currentMember.id,
          collectionId: collection.id,
          roles,
        },
      });

      setTransaction(transaction);
      dispatch(userTransaction(transaction));
    }
  };

  // TODO: remove this org sensemaker check once the catalog sensemaker migration lands
  // and all org sensemakers are given the highlighter role for all the collections in their org
  const disabled = isAdminOrOwner || !iAmAdminOrOwner || isSenseMaker;

  const props: CollectionRoleDropdownViewProps = {
    collectionId: collection.id,
    onSelect: handleCollectionRoleChange,
    loading: !!transaction && !transaction.error,
    isOrgSenseMaker: isSenseMaker,
    tooltipLabel: isAdminOrOwner
      ? t('admin.tab_team_collection_role_admin_tooltip')
      : // TODO: remove this org sensemaker check once the catalog sensemaker migration lands
      // and all org sensemakers are given the highlighter role for all the collections in their org
      isSenseMaker
      ? t('admin.tab_team_collection_role_sensemaker_tooltip')
      : '',
    collectionRole,
    disabled,
  };

  return <CollectionRoleDropdown {...props} />;
};

type CollectionRoleDropdownViewProps = {
  collectionId: Collection['id'];
  collectionRole: CollectionRole | 'None';
  onSelect: (role: CollectionRole | 'None') => void;
  loading?: boolean;
  disabled?: boolean;
  isOrgSenseMaker?: boolean;
  tooltipLabel?: string;
};

export const CollectionRoleDropdown = ({
  collectionRole,
  collectionId,
  loading,
  disabled,
  onSelect,
  isOrgSenseMaker,
  tooltipLabel,
}: CollectionRoleDropdownViewProps) => {
  const [tooltip, setTooltip] = React.useState<boolean>(false);

  return (
    <>
      <PillDropdown
        dropdownItems={[
          {
            value: CollectionRole.manager,
            theme: 'lightGray',
          },
          {
            value: CollectionRole.collaborator,
            theme: 'lightGray',
          },
          {
            value: CollectionRole.highlighter,
            theme: 'lightGray',
          },
          {
            value: CollectionRole.viewer,
            theme: 'lightGray',
            hide: isOrgSenseMaker,
          },
          {
            value: 'None',
            theme: 'lightGray',
            hide: isOrgSenseMaker,
          },
        ]}
        selectedValue={collectionRole}
        onSelect={onSelect as (val: string) => void}
        loading={loading}
        disabled={disabled}
        id={`collection-role-${collectionId}`}
        aria-label={tooltipLabel || ''}
        testId={`collection-role-${collectionId}`}
      />
      {tooltipLabel && (
        <Tooltip
          boundariesElement="window"
          placement="bottom"
          hideArrow={true}
          isOpen={tooltip}
          target={`collection-role-${collectionId}`}
          toggle={() => setTooltip((self) => !self)}
          data-testid={`tooltip-collection-role-${collectionId}`}
        >
          {tooltipLabel}
        </Tooltip>
      )}
    </>
  );
};

export default UpdateCollectionRoleDropdown;
